import type { ApiMessage } from '../../types/messages';
import type { ApiChat } from '../../types/chats';
import type { OpenImRawConversation, OpenImRawMessage } from './types';

import { mapOpenImMessageToApiMessage } from './message';
import { pickFirstDefined } from './pickFirstDefined';

export type ApiChatWithOpenImFields = ApiChat & {
  _tgimUnreadCount: number;
  // Present when the conversation carries a parsable `latestMsg` -- lets callers backfill
  // `global.chats.lastMessageIds` / `global.messages` without a second round-trip, since OpenIM's
  // conversation list (unlike the real Telegram `messages.getDialogs` payload) is otherwise the
  // ONLY place this data is available until the chat is actually opened.
  _tgimLastMessage?: ApiMessage;
};

// OpenIM conversationType: 1 = single chat, 2 = group, 3 = super group（未来若接入需在此扩展）。
export function mapOpenImConversationToApiChat(
  raw: OpenImRawConversation,
  selfUserId: string,
): ApiChatWithOpenImFields {
  const isGroup = raw.conversationType === 2 || raw.conversationType === 3;
  const unreadCount = pickFirstDefined(
    raw as Record<string, unknown>,
    ['unreadCount', 'unread', 'unreadMsgNum', 'unreadMsgCount'],
    0,
  );
  const isPinned = pickFirstDefined(raw as Record<string, unknown>, ['isPinned', 'isTop'], false);
  // Bare peer/group id the real `sendMessage` WASM call needs as `recvID`/`groupID` — never the
  // composite `conversationID` used as `id` below. The OpenIM SDK derives `conversationID` FROM
  // `userID`/`groupID`, not the other way round (`getConversationIDBySessionType` in
  // openim-sdk-core `internal/conversation_msg/api.go`), so reading them straight off the raw
  // conversation is more reliable than re-parsing the composite id's `si_`/`g_`/`sg_` prefix.
  const targetId = isGroup ? raw.groupID : raw.userID;

  // `latestMsg` is a JSON-encoded `OpenImRawMessage` (same shape `OnRecvNewMessage`/history deliver)
  // — without reading it, the chat list has no last-message preview/timestamp until the chat is
  // opened once (`loadViewportMessages` is the only other place that writes `lastMessageIds`).
  let lastMessage: ApiMessage | undefined;
  if (raw.latestMsg) {
    try {
      const parsedLatestMsg = JSON.parse(raw.latestMsg) as OpenImRawMessage;
      lastMessage = mapOpenImMessageToApiMessage(parsedLatestMsg, selfUserId);
    } catch {
      lastMessage = undefined;
    }
  }

  return {
    id: raw.conversationID,
    type: isGroup ? 'chatTypeBasicGroup' : 'chatTypePrivate',
    title: raw.showName || raw.conversationID,
    // AppDTO 内部记账字段，前缀 `_tgim` 避免和真实 ApiChat 字段撞名，均不进 ApiChat 的公开类型。
    // `_tgimIsPinned`（下一行）用它来决定要不要走 orderedPinnedIds。
    _tgimUnreadCount: unreadCount,
    _tgimIsPinned: isPinned ? true : undefined,
    _tgimTargetId: targetId,
    _tgimLastMessage: lastMessage,
    avatarBlobUrl: raw.faceURL || undefined,
  } as ApiChatWithOpenImFields;
}
